<template>
    <div ref="basemapDropdown" class="basemap-dropdown">
        <md-field class="field-dropdown-button">
            <md-select v-model="basemap" name="basemap" id="basemap" class="field-dropdown-select" md-dense>
                <md-optgroup label="Basemaps">
                    <md-option v-for="(layer, name) in basemaps" :key="layer.basemapName" :value="name"
                        class="field-dropdown-option">{{layer.basemapName}}</md-option>
               </md-optgroup>
                <md-optgroup label="Additional Layers">
                    <md-option v-for="(layer, name) in overlay_basemaps" :key="layer.basemapName" :value="name"
                        class="field-dropdown-option">{{layer.basemapName}}</md-option>
                </md-optgroup>
            </md-select>
        </md-field>
    </div>
</template>

<script>
/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */
import PlanetMapType from "./CustomMapTypes/PlanetMapType";

export default {
    name: "GoogleMapBasemapView",
    data() {
        let ddata = {
            basemap: "roadmap",
            basemaps: {
                "roadmap": {
                    basemapName: "roadmap",
                    isBasemap: true,
                    isCustom: false
                },
                "satellite": {
                    basemapName: "satellite",
                    isBasemap: true,
                    isCustom: false
                },
                "hybrid": {
                    basemapName: "hybrid",
                    isBasemap: true,
                    isCustom: false
                },
                "terrain": {
                    basemapName: "terrain",
                    isBasemap: true,
                    isCustom: false
                },

                // CUSTOM BASEMAP EXAMPLE
                // custom basemaps have the flag isCustom, this indicates basemaps that are non-google ones
                // otherwise the process is similar to overlay maps, a createMapType function must be defined which
                // points towards a map class - assumes that isCustom will return a promise
                // "NICFIBasemap": {
                //     basemapName: "NICFIBasemap",
                //     isBasemap: true,
                //     isCustom: true,
                //     createMapType: () => {
                //         return new PlanetMapType(
                //             new this.google.maps.Size(256, 256),
                //             '',
                //             'planet-tiles',
                //             'planet_medres_visual_2021-10_mosaic',
                //             '953733ef80d1407d9a51bd72643bb1c4'
                //         )
                //     }
                // },
            },
            overlay_basemaps: {
                // overlay basemaps require another basemap and must define a defaultBasemap
                // it must also define a function which returns an instance of a MapType object
                "NICFI": {
                    basemapName: "NICFI",
                    isBasemap: false,
                    isCustom: true,
                    defaultBasemap: "roadmap",
                    createMapType: () => new Promise((resolve, reject) => {
                        function getLatestProduct(){
                            // returns products in ascending date order
                            // https://developers.planet.com/docs/basemaps/reference/#operation/listMosaics
                            var productID = `planet_medres_visual_`
                            var url = new URL('https://api.planet.com/basemaps/v1/mosaics')
                            var params = {name__contains: productID, api_key:"953733ef80d1407d9a51bd72643bb1c4"}
                            url.search = new URLSearchParams(params).toString()

                            return fetch(url)
                                .then(response => response.json())
                                .then(data => data.mosaics.pop())
                        }

                        getLatestProduct().then(mosaic => {
                            console.debug("visualising latest mosaic", mosaic.name)
                            resolve(new PlanetMapType(
                                            new this.google.maps.Size(256, 256),
                                            '',
                                            'planet-tiles',
                                            mosaic.name,
                                            '953733ef80d1407d9a51bd72643bb1c4'
                                        ))
                        }).catch(err => {
                            reject(err)
                        })
                    })
                },
            },
            overlayMap: null
        };
        return ddata;
    },
    props: {
        google: {
            type: Object,
            required: true,
        },
        map: {
            type: Object,
            required: true,
        },
        orgCustomisations: {
            type: Object,
            required: true
        },
    },
    mounted() {
        const basemapDropdown = this.$refs.basemapDropdown;
        this.map.controls[this.google.maps.ControlPosition.RIGHT_TOP].push(
            basemapDropdown
        );
    },
    created() {
        if (this.defaultBasemap) {
            this.basemap = this.defaultBasemap;
        }

        if (this.roadmapName) {
            this.basemaps["roadmap"].basemapName = this.roadmapName;
        }

        if (this.ebxUseOpenAccessDashboard){
            this.basemap = 'terrain';
            this.basemaps["roadmap"].basemapName = 'administrative regions'
        }
    },
    methods:{
        changeBasemap(basemap) {
            // checks for custom basemap, register it and change to it - assumes custom basemap is promise based
            if(basemap.isCustom){
                // createMapType is an async request so use .then()
                basemap.createMapType()
                    .then(basemapMap => {
                        // adds basemap to registry
                        this.map.mapTypes.set(basemap.basemapName, basemapMap);
                        this.map.setMapTypeId(basemap.basemapName)
                    })
                    .catch(error => {
                        console.error("couldn't load basemap", error)
                    })
            } else {
                this.map.setMapTypeId(basemap.basemapName)
            }
        },
        addOverlayMap(basemap) {
            // change basemap to the required basemap by the overlay
            this.changeBasemap(this.basemaps[basemap.defaultBasemap])

            // the check for isCustom is a bit redundant, if the basemap
            // is an overlay, then it's creating a tileOverlay normally
            // meaning that it has to implement a customMapType
            // TODO consider refactoring this, as it's confusing, we need to make
            // a clear distinction between basemaps and overlays that sit on top of
            // basemaps
            // consider implementing a default method for adding normal ImageMapTypes
            if(basemap.isCustom){
                // createMapType is an async request so use .then()
                basemap.createMapType()
                    .then(basemapMap => {
                        this.overlayMap = basemapMap;
                        this.map.overlayMapTypes.insertAt(0, this.overlayMap)
                    })
                    .catch(error => {
                        console.error("couldn't load overlaymap", error)
                    })
            } else {
                console.error("implementing an overlay basemap, but the isCustom flag is missing, this will not set an overlay")
                this.map.overlayMapTypes.insertAt(0, this.overlayMap)
            }
        },
        removeOverlayMap() {
            let index = this.map.overlayMapTypes.indexOf(this.overlayMap);
            if(index >= -1) {
                this.map.overlayMapTypes.setAt(index, null)
            }
            this.overlayMap = null;
        },
        updateBasemap(newBasemap) {
            console.log("changing basemap to", newBasemap)
            let basemap = this.basemaps[newBasemap] || this.overlay_basemaps[newBasemap];
            // if an overlay exists remove it
            if(this.overlayMap){
                this.removeOverlayMap();
            }
            // if the new basemap replaces the entire map
            if(basemap.isBasemap){
                this.changeBasemap(basemap);
            } else { // else add it on top of the current basemap
                this.addOverlayMap(basemap);
            }
        }
    },
    watch: {
        // when user changes basemap selection
        basemap(newBasemap){
            this.updateBasemap(newBasemap);
        }
    },
    computed: {
        defaultBasemap() {
            if (this.orgCustomisations) {
                return this.orgCustomisations['map.basemap.default'] ? this.orgCustomisations['map.basemap.default'] : 'roadmap';
            } else {
                return 'roadmap';
            }
        },
        roadmapName() {
            if (this.orgCustomisations) {
                return this.orgCustomisations['map.roadmap.name'] ? this.orgCustomisations['map.roadmap.name'] : 'roadmap';
            } else {
                return 'roadmap';
            }
        },
    }
};
</script>